import { AxiosRequestConfig } from 'axios';

import type {
  ConsoleApiClient,
  EndpointParameters,
  EndpointReturnType,
  MiddlewareArgs,
} from '@cast/api-client';
import { makeLogger } from '@cast/utils';

import type { FixtureReaderArgs, Locator } from '../types';
import type { FileLoaders, LoaderLocator } from './types';

export const log = makeLogger('Fixtures');

/**
 * Helper to build FixtureReaderArgs
 */
const buildFixtureArgs = <
  FEATURE extends keyof ConsoleApiClient,
  ENDPOINT extends keyof ConsoleApiClient[FEATURE]
>({
  args,
  middlewareArgs,
  tag,
  commonJson,
  stateJson,
  stateData,
  fileLoaders,
  loadFixture,
}: {
  args: LoadFixtureArgs<FEATURE, ENDPOINT>;
  tag?: string | number;
  middlewareArgs?: MiddlewareArgs;
  commonJson: EndpointReturnType<FEATURE, ENDPOINT> | null;
  stateJson: EndpointReturnType<FEATURE, ENDPOINT> | null;
  stateData: EndpointReturnType<FEATURE, ENDPOINT> | null;
  fileLoaders: FileLoaders;
  loadFixture: <
    F extends keyof ConsoleApiClient,
    E extends keyof ConsoleApiClient[F]
  >(
    args: LoadFixtureArgs<F, E>
  ) => Promise<EndpointReturnType<F, E> | undefined>;
}): FixtureReaderArgs<FEATURE, ENDPOINT> => {
  const data = stateData ?? stateJson ?? commonJson;

  return {
    feature: args.feature,
    endpoint: args.endpoint,
    endpointParameters: args.endpointParameters,
    data: data ?? undefined,
    commonJson: commonJson ?? undefined,
    stateJson: stateJson ?? undefined,
    stateData: stateData ?? undefined,
    state: args.state,
    tag,
    middlewareArgs,
    loadResponseJson: fileLoaders.loadStaticJson,
    readData: async <
      F extends keyof ConsoleApiClient,
      E extends keyof ConsoleApiClient[F]
    >(
      locator: Omit<Locator<F, E>, 'fixturesGroup'>
    ): Promise<EndpointReturnType<F, E> | null> => {
      const result = await loadFixture({
        ...locator,
        state: args.state,
      } as LoadFixtureArgs<F, E>);
      return result ?? null;
    },
    loadJson: fileLoaders.loadJson,
  };
};

/**
 * Build file name from locator.
 * Examples:
 *   { feature: 'clusters', endpoint: 'getClusters' } -> 'clusters.getClusters'
 *   { feature: 'clusters', endpoint: 'getClusters', tag: 'variant-1' } -> 'clusters.getClusters.variant-1'
 */
export const buildFileName = (
  locator: Pick<Locator, 'feature' | 'endpoint' | 'tag'>
): string => {
  const baseName = `${locator.feature}.${locator.endpoint}`;
  return locator.tag ? `${baseName}.${locator.tag}` : baseName;
};

/**
 * Extract tag from endpoint parameters.
 * The last parameter is typically AxiosRequestConfig with middlewareArgs.requestMeta.tag
 */
export const retrieveMiddlewareArgs = (
  parameters: unknown
): MiddlewareArgs | undefined => {
  if (!Array.isArray(parameters)) {
    return undefined;
  }
  const lastParam = parameters[parameters.length - 1];
  if (typeof lastParam !== 'object' || lastParam === null) {
    return undefined;
  }
  const config = lastParam as AxiosRequestConfig;
  return config?.middlewareArgs;
};

/**
 * Arguments for loading a fixture
 */
export type LoadFixtureArgs<
  FEATURE extends keyof ConsoleApiClient = any,
  ENDPOINT extends keyof ConsoleApiClient[FEATURE] = any
> = {
  feature: FEATURE;
  endpoint: ENDPOINT;
  state?: string;
  tag?: string;
  endpointParameters?: EndpointParameters<FEATURE, ENDPOINT>;
};

/**
 * Creates a loadFixture function that uses the provided FileLoaders.
 * This abstracts the fallback chain logic for both browser and node environments.
 *
 * Flow:
 * 1. Load commonJson (root static JSON)
 * 2. Load stateJson (state static JSON) if state provided
 * 3. If state reader exists: Call state reader → get stateData (raw)
 * 4. Call specific root reader (if exists) with all data
 * 5. Call asterisk readers LAST (feature-level '*', then root-level '*')
 * 6. Fallback: return stateJson ?? commonJson if no readers
 */
export const makeFixtureLoader = (
  fileLoaders: FileLoaders
): (<
  FEATURE extends keyof ConsoleApiClient,
  ENDPOINT extends keyof ConsoleApiClient[FEATURE]
>(
  args: LoadFixtureArgs<FEATURE, ENDPOINT>
) => Promise<EndpointReturnType<FEATURE, ENDPOINT> | undefined>) => {
  // Create the loadFixture function that will be returned
  const loadFixture = async <
    FEATURE extends keyof ConsoleApiClient,
    ENDPOINT extends keyof ConsoleApiClient[FEATURE]
  >(
    args: LoadFixtureArgs<FEATURE, ENDPOINT>
  ): Promise<EndpointReturnType<FEATURE, ENDPOINT> | undefined> => {
    const middlewareArgs = retrieveMiddlewareArgs(args.endpointParameters);
    const tag = args.tag ?? middlewareArgs?.requestMeta?.tag;

    const loaderLocator: LoaderLocator<FEATURE, ENDPOINT> = {
      feature: args.feature,
      endpoint: args.endpoint,
      state: args.state,
      tag,
    };

    const fileName = buildFileName(loaderLocator);
    const locatorKey = `${fileName}${
      args.state ? ` [state: ${args.state}]` : ''
    }`;

    let source: string | null = null;

    // 1. Load commonJson (root static JSON)
    const commonJson = await fileLoaders.loadStaticJson(loaderLocator);
    if (commonJson !== null) {
      source = `static/${fileName}.json`;
    }

    // 2. Load stateJson (state static JSON) if state provided
    const stateJson = args.state
      ? await fileLoaders.loadStateJson(loaderLocator)
      : null;
    if (stateJson !== null) {
      source = `states/${args.state}/static/${fileName}.json`;
    }

    const fixtureArgsBase = {
      args,
      tag,
      middlewareArgs,
      commonJson,
      stateJson,
      fileLoaders,
      loadFixture,
    };

    // 3. If state reader exists: Call state reader → get stateData (raw)
    let stateData: EndpointReturnType<FEATURE, ENDPOINT> | null = null;
    if (args.state) {
      const stateReader = await fileLoaders.loadStateReader(loaderLocator);
      if (stateReader && typeof stateReader === 'function') {
        const fixtureArgs = buildFixtureArgs({
          ...fixtureArgsBase,
          stateData: null,
        });
        stateData = (await stateReader(fixtureArgs)) as EndpointReturnType<
          FEATURE,
          ENDPOINT
        > | null;
        if (stateData !== null) {
          source = `states/${args.state}/readers/${fileName}.ts`;
        }
      }
    }

    // Compute resolved data
    const data = stateData ?? stateJson ?? commonJson;

    // Build fixture args with all data for root readers
    const fixtureArgs = buildFixtureArgs({
      ...fixtureArgsBase,
      stateData,
    });

    // 4. Call specific root reader (if exists)
    const rootReader = await fileLoaders.loadStaticReader(loaderLocator);
    let result: EndpointReturnType<FEATURE, ENDPOINT> | undefined =
      data ?? undefined;

    if (rootReader && typeof rootReader === 'function') {
      const readerResult = await rootReader({
        ...fixtureArgs,
        data: result,
      });
      if (readerResult !== undefined) {
        result = readerResult as EndpointReturnType<FEATURE, ENDPOINT>;
        source = `readers/${fileName}.ts`;
      }
    }

    // 5. Call asterisk readers LAST
    // First, try feature-level asterisk reader
    const featureAsteriskLocator: LoaderLocator = {
      feature: args.feature,
      endpoint: '*' as any,
      state: args.state,
      tag,
    };
    const featureAsteriskFileName = buildFileName(featureAsteriskLocator);
    const featureAsteriskReader = await fileLoaders.loadStaticReader(
      featureAsteriskLocator
    );
    if (featureAsteriskReader && typeof featureAsteriskReader === 'function') {
      const asteriskResult = await featureAsteriskReader({
        ...fixtureArgs,
        data: result,
      });
      if (asteriskResult !== undefined) {
        result = asteriskResult as EndpointReturnType<FEATURE, ENDPOINT>;
        source = `readers/${featureAsteriskFileName}.ts`;
      }
    }

    // Then, try root-level asterisk reader
    const rootAsteriskLocator: LoaderLocator = {
      feature: '*' as any,
      endpoint: '*' as any,
      state: args.state,
      tag,
    };
    const rootAsteriskReader = await fileLoaders.loadStaticReader(
      rootAsteriskLocator
    );
    if (rootAsteriskReader && typeof rootAsteriskReader === 'function') {
      const asteriskResult = await rootAsteriskReader({
        ...fixtureArgs,
        data: result,
      });
      if (asteriskResult !== undefined) {
        result = asteriskResult as EndpointReturnType<FEATURE, ENDPOINT>;
        source = `readers/*.*.ts`;
      }
    }

    // Log the fixture load result
    log(`[${locatorKey}]`, {
      parameters: args.endpointParameters,
      source,
      result,
    });

    return result;
  };

  return loadFixture;
};
